// https://redux.js.org/tutorials/essentials/part-7-rtk-query-basics

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { auth } from '../../firebase';

export const apiSlice = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URI || 'https://api.acc.inframent.com',
    prepareHeaders: async (headers, { endpoint }) => {
      if (['register', 'login'].includes(endpoint)) {
        return headers;
      }

      if (auth.currentUser) {
        const idToken = await auth.currentUser.getIdToken();
        if (idToken) headers.set('Authorization', `Bearer ${idToken}`);
      }

      if (localStorage.getItem('authToken')) {
        const token = localStorage.getItem('authToken');
        headers.set('Authorization', `Bearer ${token}`);
      }

      return headers;
    },
  }),
  tagTypes: [
    'Profile',
    'Projects',
    'Project',
    'Map',
    'Drawings',
    'Access',
    'Surveys',
    'Survey',
    'Assets',
  ],
  endpoints: (builder) => ({
    // User Authentication Endpoints
    register: builder.mutation({
      query: (body) => ({
        url: '/auth/register',
        method: 'POST',
        body,
      }),
    }),
    login: builder.mutation({
      query: (body) => ({
        url: '/auth/login',
        method: 'POST',
        body,
      }),
    }),
    // Profile
    getProfile: builder.query({
      query: () => '/profile',
      providesTags: ['Profile'],
    }),
    postProfile: builder.mutation({
      query: (body) => ({
        url: '/profile',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Profile'],
    }),

    // Assets
    getAssets: builder.query({
      query: (projectId) => `/projects/${projectId}/assets`, // Define API route
      providesTags: ['Assets'],
    }),
    getAsset: builder.query({
      query: (projectId, assetId) => `/projects/${projectId}/assets/${assetId}`,
      providesTags: ['Assets'],
    }),
    postAsset: builder.mutation({
      query: ({ projectId, asset }) => ({
        url: `/projects/${projectId}/assets/`,
        method: 'POST',
        body: asset,
      }),
      invalidatesTags: ['Assets', 'Surveys'],
    }),
    putAsset: builder.mutation({
      query: ({ projectId, assetId, ...updates }) => ({
        url: `/projects/${projectId}/assets/${assetId}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: ['Assets', 'Surveys'],
    }),
    putAssetBatch: builder.mutation({
      query: ({ projectId, asset_ids, ...updates }) => ({
        url: `/projects/${projectId}/assets/batch`,
        method: 'PUT',
        body: { asset_ids, ...updates },
      }),
      invalidatesTags: ['Assets', 'Surveys'],
    }),
    getAssetPhotoUploadUrl: builder.mutation({
      query: ({ projectId, payload }) => ({
        url: `/projects/${projectId}/assets/photos/upload-url`,
        method: 'POST',
        body: payload,
      }),
    }),
    confirmAssetPhotos: builder.mutation({
      query: ({ projectId, asset_id, photo_ids }) => ({
        url: `/projects/${projectId}/assets/photos/confirm`,
        method: 'POST',
        body: { asset_id, photo_ids },
      }),
      invalidatesTags: ['Assets', 'Surveys'],
    }),
    deleteAssetPhoto: builder.mutation({
      query: ({ projectId, photoId }) => ({
        url: `/projects/${projectId}/assets/photos/${photoId}`,
        method: 'DELETE',
      }),
    }),
    deleteAssets: builder.mutation({
      query: ({ projectId, assetIds }) => ({
        url: `/projects/${projectId}/assets/${assetIds}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Assets'],
    }),

    // Projects
    getProjects: builder.query({
      query: () => '/projects',
      providesTags: ['Projects'],
    }),
    getProject: builder.query({
      query: (projectId) => `/projects/${projectId}`,
      providesTags: ['Project'],
    }),
    getProjectMap: builder.query({
      query: (projectId) => `/projects/${projectId}/map`,
      providesTags: ['Project', 'Map'],
    }),
    postProject: builder.mutation({
      query: (initialProject) => ({
        url: '/projects',
        method: 'POST',
        body: initialProject,
      }),
      invalidatesTags: ['Projects'],
    }),
    deleteProject: builder.mutation({
      query: (projectId) => ({
        url: `/projects/${projectId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Projects'],
    }),

    // Drawings
    postProjectDrawings: builder.mutation({
      query: ({ projectId, zip }) => ({
        url: `/projects/${projectId}/drawings`,
        method: 'POST',
        body: { zip },
      }),
      invalidatesTags: ['Drawings', 'Map'],
    }),
    getProjectDrawings: builder.query({
      query: (projectId) => `/projects/${projectId}/drawings`,
      providesTags: ['Drawings'],
    }),
    postProjectDrawingId: builder.mutation({
      query: ({ projectId, id, type, name, color }) => ({
        url: `/projects/${projectId}/drawings/${id}`,
        method: 'POST',
        body: {
          type,
          name,
          color,
        },
      }),
      invalidatesTags: ['Map', 'Drawings'],
    }),
    deleteDrawings: builder.mutation({
      query: ({ projectId, drawingIds }) => ({
        url: `/projects/${projectId}/drawings/${drawingIds}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Map', 'Drawings'],
    }),

    // Access
    getProjectAccess: builder.query({
      query: (projectId) => `/projects/${projectId}/access`,
      providesTags: ['Access'],
    }),
    postProjectAccess: builder.mutation({
      query: ({ projectId, email, phone, role }) => ({
        url: `/projects/${projectId}/access`,
        method: 'POST',
        body: { email, phone, role },
      }),
      invalidatesTags: ['Access'],
    }),
    postProjectAccessUserId: builder.mutation({
      query: ({ projectId, userId, role }) => ({
        url: `/projects/${projectId}/access/${userId}`,
        method: 'POST',
        body: { role },
      }),
      invalidatesTags: ['Access'],
    }),

    // Surveys
    getSurveys: builder.query({
      query: ({ projectId, includeMembers = false, includeEdges = false }) =>
        `/projects/${projectId}/surveys?includeMembers=${includeMembers}&includeEdges=${includeEdges}`,
      providesTags: ['Surveys'],
    }),
    deleteSurveyId: builder.mutation({
      query: ({ projectId, surveyId }) => ({
        url: `/projects/${projectId}/surveys/${surveyId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Surveys'],
    }),
    exportSurveys: builder.mutation({
      query: ({ projectId, surveyIds }) => ({
        url: `/projects/${projectId}/export-surveys/?surveyIds=${surveyIds}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),
    // Survey update methods
    patchSurveyLocation: builder.mutation({
      query: ({ projectId, surveyId, latitude, longitude }) => ({
        url: `/projects/${projectId}/surveys/${surveyId}/location`,
        method: 'PATCH',
        body: { latitude, longitude },
      }),
    }),
    patchSurveyPhoto: builder.mutation({
      query: ({ projectId, surveyId, uuid }) => ({
        url: `/projects/${projectId}/surveys/${surveyId}/uuid`,
        method: 'PATCH',
        body: { uuid },
      }),
    }),

    // New Survey Routes
    // Survey members (ordered)
    getSurveyMembers: builder.query({
      // arg: { projectId, surveyId }
      query: ({ projectId, surveyId }) => `/projects/${projectId}/survey-members/${surveyId}`,
      providesTags: (result, error, arg) => [{ type: 'Survey', id: arg.surveyId }, 'Surveys'],
    }),
    getSurveyMembersByProjectId: builder.query({
      query: (projectId) => `/projects/${projectId}/survey-members`,
      providesTags: ['Surveys'],
    }),
    getSurveysEdgesByProjectId: builder.query({
      query: (projectId) => `/projects/${projectId}/survey-edges`,
      providesTags: ['Surveys'],
    }),
    postSurveyMember: builder.mutation({
      // arg: { projectId, surveyId, member: { asset_id, seq_index?, metadata? } }
      query: ({ projectId, surveyId, member }) => ({
        url: `/projects/${projectId}/survey-members/${surveyId}`,
        method: 'POST',
        body: { member },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Survey', id: arg.surveyId },
        'Surveys',
        'Assets',
      ],
    }),
    postSurveyMemberBatch: builder.mutation({
      query: ({ projectId, surveyId, members }) => ({
        url: `/projects/${projectId}/survey-members/${surveyId}/batch`,
        method: 'POST',
        body: { members },
      }),
    }),
    postSurvey: builder.mutation({
      // arg: { projectId, lat, lng, alt?, uuid?, note? }
      query: ({ projectId, lat, lng, alt = 0, uuid = null, note = null }) => ({
        url: `/projects/${projectId}/surveys`,
        method: 'POST',
        body: { lat, lng, alt, uuid, note },
      }),
      invalidatesTags: ['Surveys'],
    }),
    deleteSurveyMember: builder.mutation({
      // arg: { projectId, surveyId, assetId }
      query: ({ projectId, surveyId, assetId }) => ({
        url: `/projects/${projectId}/survey-members/${surveyId}/${assetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Survey', id: arg.surveyId },
        'Surveys',
        'Assets',
      ],
    }),
    reorderSurveyMember: builder.mutation({
      // arg: { projectId, surveyId, assetId, seq_index }
      query: ({ projectId, surveyId, assetId, seq_index }) => ({
        url: `/projects/${projectId}/survey-members/${surveyId}/${assetId}`,
        method: 'PUT', // or PATCH if you use patch semantics
        body: { seq_index },
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Survey', id: arg.surveyId }, 'Surveys'],
    }),

    // Route GeoJSON for a survey (cached route_geom preferred)
    getSurveyRoute: builder.query({
      // arg: { projectId, surveyId }
      query: ({ projectId, surveyId }) => `/projects/${projectId}/surveys/${surveyId}/route`,
      providesTags: (result, error, arg) => [{ type: 'Survey', id: arg.surveyId }, 'Surveys'],
      // No transformResponse - backend returns GeoJSON object or null
    }),

    // Asset edges (graph)
    createSequentialEdges: builder.mutation({
      // arg: { projectId, surveyId }
      query: ({ projectId, surveyId }) => ({
        url: `/projects/${projectId}/survey-edges/${surveyId}/sequential`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Survey', id: arg.surveyId },
        'Survey',
        'Surveys',
      ],
    }),
    postAssetEdge: builder.mutation({
      // arg: { projectId, surveyId, edge: { asset_a_id, asset_b_id, directed?, relation_type?, metadata? } }
      query: ({ projectId, surveyId, edge }) => ({
        url: `/projects/${projectId}/survey-edges/${surveyId}`,
        method: 'POST',
        body: edge,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Survey', id: arg.surveyId }, 'Survey'],
    }),
    getAssetEdges: builder.query({
      // arg: { projectId, surveyId }
      query: ({ projectId, surveyId }) => `/projects/${projectId}/survey-edges/${surveyId}`,
      providesTags: (result, error, arg) => [{ type: 'Survey', id: arg.surveyId }, 'Survey'],
    }),
    deleteAssetEdge: builder.mutation({
      // arg: { projectId, surveyId, edgeId }
      query: ({ projectId, surveyId, edgeId }) => ({
        url: `/projects/${projectId}/survey-edges/${surveyId}/${edgeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Survey', id: arg.surveyId }, 'Survey'],
    }),

    // Get neighbors (adjacency) for an asset within a survey
    getNeighbors: builder.query({
      // arg: { projectId, surveyId, assetId }
      query: ({ projectId, surveyId, assetId }) =>
        `/projects/${projectId}/surveys/${surveyId}/assets/${assetId}/neighbors`,
      providesTags: (result, error, arg) => [{ type: 'Survey', id: arg.surveyId }, 'Assets'],
    }),

    // Vectors
    getVectors: builder.query({
      query: (projectId) => `/projects/${projectId}/vectors`,
      providesTags: ['Vectors'],
      transformResponse: (data) => {
        for (const d of data) {
          d.key = d.drawing_id;
        }
        return data;
      },
    }),
    exportVectors: builder.mutation({
      query: ({ projectId, drawingIds }) => ({
        url: `/projects/${projectId}/export-vectors/?drawingIds=${drawingIds}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),
    exportDWG: builder.mutation({
      // Custom queryFn to fetch, handle the blob, trigger download, and only store a serializable flag
      async queryFn({ projectId, espg, scale, assetsSelected }, _api, _extraOptions, fetchWithBQ) {
        try {
          const ids = assetsSelected.map((a) => a.id).join(',');
          // Use fetchWithBQ to leverage RTK Query's baseQuery and responseHandler
          const response = await fetchWithBQ({
            url: `/projects/${projectId}/export-dwg?espg=${espg}&scale=${scale}&ids=${ids}`,
            method: 'GET',
            responseHandler: (res) => res.blob(),
          });

          if (response.error) {
            return { error: response.error };
          }

          const blob = response.data;

          // Trigger download as a side effect
          const fileURL = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = fileURL;
          a.download = `export_${projectId}.zip`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(fileURL);

          // Only return a serializable flag
          return { data: { success: true } };
        } catch (err) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: err.message,
            },
          };
        }
      },
    }),
    // APK metadata + S3 download_url (prefix from API process.env.ENV)
    getApkMeta: builder.query({
      query: () => `/apk/meta`,
    }),
    getAssetsFromSurvey: builder.query({
      query: ({ projectId, surveyIds, fetchPhotos = false, fetchAssets = true }) =>
        `/projects/${projectId}/surveys-assets/assets?surveyIds=${surveyIds.join(',')}&fetchPhotos=${fetchPhotos}&fetchAssets=${fetchAssets}`,
      providesTags: ['Surveys'],
    }),

    // Merge Surveys
    mergeSurveys: builder.mutation({
      query: ({ projectId, surveyIds, targetSurveyId, newAssetsToConnect }) => ({
        url: `/projects/${projectId}/merge-surveys`,
        method: 'POST',
        body: { surveyIds, targetSurveyId, newAssetsToConnect },
      }),
      invalidatesTags: ['Surveys'],
    }),
  }),
});

export const {
  useRegisterMutation,
  useLoginMutation,

  useGetProfileQuery,
  usePostProfileMutation,

  useGetAssetsQuery,
  usePostAssetMutation,
  usePutAssetMutation,
  usePutAssetBatchMutation,
  useGetAssetPhotoUploadUrlMutation,
  useConfirmAssetPhotosMutation,
  useDeleteAssetPhotoMutation,
  useDeleteAssetsMutation,

  useGetProjectsQuery,
  useGetProjectQuery,
  useGetProjectMapQuery,
  usePostProjectMutation,
  useDeleteProjectMutation,

  useGetProjectDrawingsQuery,
  usePostProjectDrawingsMutation,
  usePostProjectDrawingIdMutation,
  useDeleteDrawingsMutation,

  useGetProjectAccessQuery,
  usePostProjectAccessMutation,
  usePostProjectAccessUserIdMutation,

  useGetSurveysQuery,
  useDeleteSurveyIdMutation,
  useExportSurveysMutation,
  usePatchSurveyLocationMutation,
  usePatchSurveyPhotoMutation,

  useGetVectorsQuery,
  useExportVectorsMutation,
  useExportDWGMutation,

  useGetApkMetaQuery,

  // New Survey Routes
  useGetSurveyMembersQuery,
  useGetSurveyMembersByProjectIdQuery,
  useGetSurveysEdgesByProjectIdQuery,
  usePostSurveyMemberMutation,
  useDeleteSurveyMemberMutation,
  usePostSurveyMemberBatchMutation,
  useReorderSurveyMemberMutation,
  useGetSurveyRouteQuery,
  useCreateSequentialEdgesMutation,
  usePostAssetEdgeMutation,
  useGetAssetEdgesQuery,
  useDeleteAssetEdgeMutation,
  useGetNeighborsQuery,
  usePostSurveyMutation,
  useMergeSurveysMutation,
  useGetAssetsFromSurveyQuery,
} = apiSlice;
